import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useOutletContext, useLocation, Link } from "react-router-dom";
import {
    LayoutDashboard, FileStack, Map as MapIcon, RefreshCcw, Wallet, BarChart3, FileBarChart,
    Settings, Sparkles, LogOut, Moon, Sun, Check, Bell, X, AlertTriangle, Landmark, CalendarClock, GraduationCap, Mail, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme, ACCENTS, SURFACES } from "@/context/ThemeContext";
import AIChatPanel from "./AIChatPanel";
import TourPlayer from "./TourPlayer";
import WhatsNew from "./WhatsNew";
import { TOURS } from "@/lib/tours";
import { useLanguage, LangSwitcher } from "@/context/LanguageContext";
import { tr } from "@/lib/i18n";
import { APP_VERSION } from "@/lib/version";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
    { to: "/dashboard", label: "Dashboard", tkey: "nav.dashboard", icon: LayoutDashboard },
    { to: "/applications", label: "Applications", tkey: "nav.applications", icon: FileStack },
    { to: "/map", label: "Map View", tkey: "nav.map", icon: MapIcon },
    { to: "/circulations", label: "Circulations", tkey: "nav.circulations", icon: RefreshCcw },
    { to: "/financials", label: "Financials", tkey: "nav.financials", icon: Wallet },
    { to: "/analytics", label: "Analytics", tkey: "nav.analytics", icon: BarChart3 },
    { to: "/reports", label: "Reports", tkey: "nav.reports", icon: FileBarChart },
    { to: "/admin", label: "Administration", tkey: "nav.administration", icon: Settings, admin: true },
];

const ROUTE_TOURS = [
    [/^\/dashboard/, "dashboard"],
    [/^\/applications\/[^/]+/, "applicationDetail"],
    [/^\/applications/, "applications"],
    [/^\/map/, "map"],
    [/^\/circulations/, "circulations"],
    [/^\/financials/, "financials"],
    [/^\/analytics/, "analytics"],
    [/^\/reports/, "reports"],
    [/^\/admin/, "admin"],
];

export default function AppShell() {
    const { user, logout } = useAuth();
    const { prefs, update } = useTheme();
    const { t, lang } = useLanguage();
    const navigate = useNavigate();
    const [aiOpen, setAiOpen] = useState(false);
    const [aiContext, setAiContext] = useState({ type: "general", id: null });
    const [preset, setPreset] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [tourOpen, setTourOpen] = useState(false);
    const location = useLocation();
    const tourKey = (ROUTE_TOURS.find(([re]) => re.test(location.pathname)) || [])[1];
    const tour = tourKey ? TOURS[tourKey] : null;

    useEffect(() => {
        api.get("/notifications").then((r) => setNotifications(r.data)).catch(() => {});
    }, []);

    const dismissNotification = async (id) => {
        setNotifications((n) => n.filter((x) => x.id !== id));
        try { await api.post("/notifications/dismiss", { notification_id: id }); } catch {}
    };

    const openAI = (ctx = {}, presetMessage = null) => {
        setAiContext({ type: ctx.type || "general", id: ctx.id || null });
        setPreset(presetMessage);
        setAiOpen(true);
    };

    const [showMail, setShowMail] = useState(false);
    const [mailCfg, setMailCfg] = useState({ email: "", imap_host: "outlook.office365.com", imap_port: 993, password: "", enabled: false });
    const [mailBusy, setMailBusy] = useState(false);
    const [mailMsg, setMailMsg] = useState("");

    useEffect(() => {
        api.get("/me/mail-config").then(({ data }) => setMailCfg((m) => ({ ...m, ...data, password: "" }))).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openMailCfg = () => {
        setShowMail(true);
        setMailMsg("");
    };
    const saveMailCfg = async () => {
        setMailBusy(true);
        setMailMsg("");
        try {
            await api.put("/me/mail-config", mailCfg);
            setMailMsg(mailCfg.enabled ? "Notification emails enabled for this mailbox." : "Mail settings saved.");
        } catch { setMailMsg("Could not save mail settings."); } finally { setMailBusy(false); }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const hour = new Date().getHours();
    const greeting = t(hour < 12 ? "greeting.morning" : hour < 18 ? "greeting.afternoon" : "greeting.evening");

    return (
        <div className="min-h-screen app-canvas flex">
            {/* Slim icon rail */}
            <aside data-testid="app-sidebar" className="w-[88px] shrink-0 sticky top-0 h-screen z-40 p-3 print:hidden">
                <div className="h-full rounded-[26px] bg-navy flex flex-col items-center py-4 shadow-[0_24px_60px_-24px_rgba(10,37,64,0.55)] border border-white/5">
                    <Link to="/dashboard" data-testid="sidebar-logo" title="LandLogic NexusOne"
                        className="w-11 h-11 rounded-[14px] bg-white/10 border border-white/10 flex items-center justify-center overflow-hidden hover:bg-white/15 hover:scale-105 transition-all">
                        <img src="/assets/logo-icon.png" alt="LandLogic NexusOne" className="w-9 h-9 object-contain" />
                    </Link>
                    <nav className="flex-1 flex flex-col items-center gap-1.5 mt-9">
                        {NAV.filter((n) => !n.admin || user?.role === "admin").map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                title={t(n.tkey)}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `w-11 h-11 rounded-[14px] flex items-center justify-center transition-all duration-200 ${
                                        isActive
                                            ? "bg-teal text-white shadow-[0_10px_24px_-8px_hsla(var(--brand),0.7)] scale-105"
                                            : "text-white/45 hover:text-white hover:bg-white/10 hover:scale-105"
                                    }`
                                }
                            >
                                <n.icon className="w-[19px] h-[19px]" />
                            </NavLink>
                        ))}
                    </nav>
                    <button
                        data-testid="rail-theme-toggle"
                        title={prefs.dark ? tr("Light mode") : tr("Dark mode")}
                        onClick={() => update({ dark: !prefs.dark })}
                        className="w-11 h-11 rounded-[14px] flex items-center justify-center text-white/45 hover:text-white hover:bg-white/10 transition-all mb-1.5"
                    >
                        {prefs.dark ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
                    </button>
                    <button
                        data-testid="rail-logout-btn"
                        title={tr("Sign out")}
                        onClick={handleLogout}
                        className="w-11 h-11 rounded-[14px] flex items-center justify-center text-white/45 hover:text-[#FF8FA3] hover:bg-white/10 transition-all mb-2.5"
                    >
                        <LogOut className="w-[18px] h-[18px]" />
                    </button>
                    <div className="w-9 h-9 rounded-full bg-teal flex items-center justify-center text-white text-[11px] font-bold ring-2 ring-white/20" title={user?.name}>
                        {user?.name?.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                    </div>
                </div>
            </aside>

            <div className="flex-1 min-w-0 flex flex-col">
                <header className="h-20 flex items-center justify-between px-8 gap-4 print:hidden">
                    <div>
                        <div className="font-heading font-bold text-lg text-navy dark:text-white" data-testid="header-greeting">
                            {greeting}, {user?.name?.split(" ")[0]}
                        </div>
                        <div className="text-[11px] text-slate-400 dark:text-slate-500 font-data">
                            {new Date().toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { weekday: "long", month: "long", day: "numeric" })} · {APP_VERSION} · {tr("AI-native")}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <LangSwitcher testid="staff-lang-switcher" />
                        {tour && (
                            <button data-testid="page-tour-btn" onClick={() => setTourOpen(true)} title={`${tr("Guided tour")}: ${tour.title}`}
                                className="flex items-center gap-2 px-3.5 py-2.5 rounded-[12px] text-[13px] font-semibold text-slate-600 dark:text-slate-300 bg-white/70 dark:bg-white/5 border border-white/60 dark:border-white/10 hover:shadow-md hover:text-[#0092B0] transition-all">
                                <GraduationCap className="w-[18px] h-[18px]" /> {t("common.guide")}
                            </button>
                        )}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button data-testid="notifications-bell" className="relative w-10 h-10 rounded-[12px] bg-white/70 dark:bg-white/5 border border-white/60 dark:border-white/10 flex items-center justify-center text-slate-500 dark:text-slate-300 hover:shadow-md transition-all">
                                    <Bell className="w-[18px] h-[18px]" />
                                    {notifications.length > 0 && (
                                        <span data-testid="notification-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF476F] text-white text-[10px] font-bold flex items-center justify-center">
                                            {notifications.length}
                                        </span>
                                    )}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-[380px] p-0 rounded-[16px] overflow-hidden" data-testid="notifications-panel">
                                <div className="px-5 py-4 bg-navy text-white flex items-center justify-between">
                                    <div>
                                        <div className="font-heading font-bold text-sm">{tr("Reminder Mailbox")}</div>
                                        <div className="text-[11px] text-white/55">{notifications.length} item{notifications.length !== 1 ? "s" : ""} need attention</div>
                                    </div>
                                    <Bell className="w-4 h-4 text-[#7CD4EA]" />
                                </div>
                                <div className="max-h-[380px] overflow-y-auto">
                                    {notifications.length === 0 && (
                                        <div className="py-10 text-center text-sm text-slate-400" data-testid="notifications-empty">{t("shell.no_notifications")}</div>
                                    )}
                                    {notifications.map((n, i) => (
                                        <div key={n.id} data-testid={`notification-item-${i}`}
                                            className="flex items-start gap-3 px-5 py-3.5 border-b border-slate-100 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors cursor-pointer"
                                            onClick={() => navigate(n.link)}>
                                            <div className={`w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0 ${n.severity === "high" ? "bg-[#EF476F]/10" : "bg-[#FFB703]/12"}`}>
                                                {n.type === "circulation" && <AlertTriangle className={`w-4 h-4 ${n.severity === "high" ? "text-[#EF476F]" : "text-[#B8860B]"}`} />}
                                                {n.type === "lc" && <Landmark className={`w-4 h-4 ${n.severity === "high" ? "text-[#EF476F]" : "text-[#B8860B]"}`} />}
                                                {n.type === "event" && <CalendarClock className="w-4 h-4 text-[#B8860B]" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[13px] font-bold text-navy dark:text-slate-100 leading-snug">{n.title}</div>
                                                <div className="text-[11px] text-slate-400 mt-0.5">{n.detail}</div>
                                            </div>
                                            <button data-testid={`notification-dismiss-${i}`} title={tr("Dismiss")}
                                                onClick={(e) => { e.stopPropagation(); dismissNotification(n.id); }}
                                                className="p-1.5 rounded-[8px] text-slate-300 hover:text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors shrink-0">
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button data-testid="theme-customizer-btn" className="flex items-center gap-2 px-3.5 py-2.5 rounded-[12px] text-[13px] font-semibold text-slate-600 dark:text-slate-300 bg-white/70 dark:bg-white/5 border border-white/60 dark:border-white/10 hover:shadow-md transition-all">
                                    <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: ACCENTS.find((a) => a.id === prefs.accent)?.hex }} />
                                    {tr("Personalize")}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64 p-3 rounded-[14px]">
                                <DropdownMenuLabel className="text-xs uppercase tracking-[0.15em] text-slate-400">{tr("Accent")}</DropdownMenuLabel>
                                <div className="flex gap-2 px-2 py-2">
                                    {ACCENTS.map((a) => (
                                        <button
                                            key={a.id}
                                            data-testid={`accent-${a.id}`}
                                            onClick={() => update({ accent: a.id })}
                                            className="w-7 h-7 rounded-full flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
                                            style={{ backgroundColor: a.hex }}
                                            title={a.name}
                                        >
                                            {prefs.accent === a.id && <Check className="w-3.5 h-3.5 text-white" />}
                                        </button>
                                    ))}
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-xs uppercase tracking-[0.15em] text-slate-400">{tr("Workspace tint")}</DropdownMenuLabel>
                                <div className="flex gap-2 px-2 py-2">
                                    {SURFACES.map((s) => (
                                        <button
                                            key={s.id}
                                            data-testid={`surface-${s.id}`}
                                            onClick={() => update({ surface: s.id })}
                                            className="w-7 h-7 rounded-[8px] flex items-center justify-center border border-slate-300 dark:border-white/20 transition-transform hover:scale-110 active:scale-95"
                                            style={{ backgroundColor: s.hex }}
                                            title={s.name}
                                        >
                                            {prefs.surface === s.id && <Check className="w-3.5 h-3.5 text-slate-600" />}
                                        </button>
                                    ))}
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem data-testid="toggle-dark-mode" onClick={() => update({ dark: !prefs.dark })} className="gap-2 cursor-pointer">
                                    {prefs.dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                                    {prefs.dark ? tr("Light mode") : tr("Dark mode")}
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid="toggle-density" onClick={() => update({ density: prefs.density === "compact" ? "comfortable" : "compact" })} className="gap-2 cursor-pointer">
                                    <LayoutDashboard className="w-4 h-4" />
                                    {prefs.density === "compact" ? tr("Comfortable density") : tr("Compact density")}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <button
                            data-testid="open-ai-btn"
                            onClick={() => openAI()}
                            className="brand-btn flex items-center gap-2 px-4 py-2.5 rounded-[12px] text-[13px] font-bold"
                        >
                            <Sparkles className="w-4 h-4" />
                            {t("common.ai_assistant")}
                        </button>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button data-testid="user-menu-btn" className="flex items-center gap-2.5 pl-1.5 pr-2 py-1.5 rounded-[14px] bg-white/70 dark:bg-white/5 border border-white/60 dark:border-white/10 hover:shadow-md transition-all">
                                    <div className="w-9 h-9 rounded-full bg-navy dark:bg-teal flex items-center justify-center text-white text-xs font-bold">
                                        {user?.name?.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                                    </div>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-72 p-0 rounded-[16px] overflow-hidden">
                                <div className="bg-navy p-5 text-white">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-teal flex items-center justify-center text-white text-sm font-bold ring-2 ring-white/25">
                                            {user?.name?.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-heading font-bold text-[15px]">{user?.name}</div>
                                            <div className="text-[11px] text-white/60 capitalize">{user?.role} · {user?.organization}</div>
                                        </div>
                                    </div>
                                    <div className="mt-3 text-[11px] font-data text-white/50 truncate">{user?.email}</div>
                                </div>
                                <div className="p-2">
                                    <div className="px-3 py-2">
                                        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-2">{tr("Theme accent")}</div>
                                        <div className="flex gap-2">
                                            {ACCENTS.map((a) => (
                                                <button
                                                    key={a.id}
                                                    data-testid={`user-accent-${a.id}`}
                                                    onClick={() => update({ accent: a.id })}
                                                    className="w-6 h-6 rounded-full flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
                                                    style={{ backgroundColor: a.hex }}
                                                    title={a.name}
                                                >
                                                    {prefs.accent === a.id && <Check className="w-3 h-3 text-white" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <DropdownMenuItem data-testid="user-toggle-dark" onClick={() => update({ dark: !prefs.dark })} className="gap-2 cursor-pointer rounded-[10px]">
                                        {prefs.dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                                        {prefs.dark ? tr("Switch to light") : tr("Switch to dark")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem data-testid="mail-config-btn" onClick={openMailCfg} className="gap-2 cursor-pointer rounded-[10px]">
                                        <Mail className="w-4 h-4" /> {tr("Notification email (IMAP)")}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="gap-2 cursor-pointer rounded-[10px] text-[#EF476F]">
                                        <LogOut className="w-4 h-4" /> {t("shell.signout")}
                                    </DropdownMenuItem>
                                </div>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                <main className="flex-1 px-8 pb-10 max-w-[1600px] w-full mx-auto">
                    <Outlet context={{ openAI }} />
                </main>
            </div>

            {showMail && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A2540]/50 backdrop-blur-sm p-4" data-testid="mail-config-dialog">
                    <div className="bg-white dark:bg-[#0d2c4d] rounded-[16px] border border-slate-200/70 dark:border-white/10 shadow-2xl w-full max-w-md p-6">
                        <div className="flex items-center justify-between mb-1">
                            <h3 className="font-heading font-bold text-navy dark:text-white flex items-center gap-2"><Mail className="w-4 h-4 text-[#0092B0]" /> {tr("Notification email (IMAP)")}</h3>
                            <button data-testid="mail-config-close" onClick={() => setShowMail(false)} className="p-1.5 rounded-[8px] text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"><X className="w-4 h-4" /></button>
                        </div>
                        <p className="text-[11px] text-slate-400 mb-4">{tr("Automatic notifications are delivered in-app today; enable email delivery through your mailbox. Tenant-wide rollout goes live with the Exchange Online integration.")}</p>
                        <div className="space-y-3">
                            <input data-testid="mail-email" type="email" value={mailCfg.email} onChange={(e) => setMailCfg({ ...mailCfg, email: e.target.value })} placeholder={tr("Email address")}
                                className="w-full rounded-[8px] border border-slate-200 dark:border-white/10 bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]/40" />
                            <div className="grid grid-cols-[1fr_110px] gap-2">
                                <input data-testid="mail-host" value={mailCfg.imap_host} onChange={(e) => setMailCfg({ ...mailCfg, imap_host: e.target.value })} placeholder={tr("IMAP host")}
                                    className="rounded-[8px] border border-slate-200 dark:border-white/10 bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]/40" />
                                <input data-testid="mail-port" type="number" value={mailCfg.imap_port} onChange={(e) => setMailCfg({ ...mailCfg, imap_port: Number(e.target.value) })} placeholder="993"
                                    className="rounded-[8px] border border-slate-200 dark:border-white/10 bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]/40" />
                            </div>
                            <input data-testid="mail-password" type="password" value={mailCfg.password} onChange={(e) => setMailCfg({ ...mailCfg, password: e.target.value })} placeholder={tr("Mailbox password (stored securely, never shown)")}
                                className="w-full rounded-[8px] border border-slate-200 dark:border-white/10 bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]/40" />
                            <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-600 dark:text-slate-300 cursor-pointer">
                                <input type="checkbox" data-testid="mail-enabled" checked={mailCfg.enabled} onChange={(e) => setMailCfg({ ...mailCfg, enabled: e.target.checked })} className="w-4 h-4 accent-[#00B4D8]" />
                                {tr("Send me notification emails (resubmissions, circulations, inspections)")}
                            </label>
                            {mailMsg && <div className="text-xs font-semibold text-[#05A37E]" data-testid="mail-config-msg">{mailMsg}</div>}
                            <button data-testid="mail-config-save" onClick={saveMailCfg} disabled={mailBusy || !mailCfg.email}
                                className="brand-btn w-full py-3 rounded-[10px] font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-2">
                                {mailBusy && <Loader2 className="w-4 h-4 animate-spin" />} {tr("Save mail settings")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <AIChatPanel
                open={aiOpen}
                onClose={() => setAiOpen(false)}
                contextType={aiContext.type}
                contextId={aiContext.id}
                preset={preset}
                onPresetConsumed={() => setPreset(null)}
            />
            {tour && <TourPlayer steps={tour.steps} title={tour.title} open={tourOpen} onClose={() => setTourOpen(false)} />}
            <WhatsNew />
        </div>
    );
}

export const useAppShell = () => useOutletContext();
